import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { TrendingUp } from "lucide-react";

const TEAL = "#00A8B3";
const RED = "#FF4D4F";
const GREEN = "#28A745";
const GRAY = "#D1D5DB";
const ORANGE = "#F59E0B";
const NAVY = "#1A1F36";
const MID = "#6B7280";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const letterAnim = {
  hidden: { opacity: 0, y: 15 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.025, duration: 0.35 } }),
};

const AnimatedCounter = ({ target, suffix = "%", inView, delay = 0 }) => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!inView) return;
    setCount(0);
    const timeout = setTimeout(() => {
      let start = 0;
      const dur = 1800;
      const step = target / (dur / 16);
      const timer = setInterval(() => {
        start += step;
        if (start >= target) { setCount(target); clearInterval(timer); }
        else setCount(Math.floor(start));
      }, 16);
      return () => clearInterval(timer);
    }, delay);
    return () => clearTimeout(timeout);
  }, [inView, target, delay]);
  if (count === null) return <span style={{ opacity: 0.3 }}>{target}{suffix}</span>;
  return <motion.span animate={{ scale: [1, 1.08, 1] }} transition={{ delay: delay / 1000 + 1.5, duration: 0.3 }}>{count}{suffix}</motion.span>;
};

const AnimatedText = ({ text, inView }) => (
  <span>
    {text.split("").map((char, i) => (
      <motion.span key={i} custom={i} variants={letterAnim} initial="hidden" animate={inView ? "visible" : "hidden"}
        style={{ display: 'inline-block' }}>
        {char === " " ? "\u00A0" : char}
      </motion.span>
    ))}
  </span>
);

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="px-3 py-2 rounded-lg shadow-lg text-xs" style={{ background: 'white', border: `1px solid ${TEAL}20` }}>
        <p className="font-bold" style={{ color: NAVY }}>{payload[0].name || payload[0].payload.name}</p>
        <p style={{ color: TEAL }}>{payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

const pieData = [
  { title: "Would Use AI Intake?", caption: "69% of respondents would use AI intake during waits. Our Dr. Mitra reduces doctor load by providing structured context upfront.",
    data: [{ name: "Yes, definitely", value: 69 }, { name: "Maybe", value: 31 }], colors: [TEAL, GRAY] },
  { title: "Remote Management Valuable?", caption: "79% find remote health management extremely valuable. Dr. Saathi provides 24/7 patient support for follow-ups and reminders.",
    data: [{ name: "Extremely", value: 79 }, { name: "Somewhat", value: 19 }, { name: "Not really", value: 2 }], colors: [GREEN, GRAY, "#E5E7EB"] },
  { title: "Reminders Increase Trust?", caption: "73% say automated reminders increase trust in their healthcare provider. Our system handles this automatically.",
    data: [{ name: "Yes", value: 73 }, { name: "Maybe", value: 26 }, { name: "No", value: 1 }], colors: [TEAL, GRAY, "#E5E7EB"] },
];

const painData = [
  { name: "Doctors lack context", value: 74, fill: TEAL },
  { name: "History struggles", value: 79, fill: RED },
  { name: "Failed to find report", value: 57, fill: ORANGE },
];

const waitData = [
  { name: "30+ min", value: 31, fill: RED },
  { name: "10-30 min", value: 44, fill: ORANGE },
  { name: "0-10 min", value: 24, fill: GREEN },
];

const scrollTo = (e, href) => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };

const PieChartCard = ({ item, index, inView }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, scale: 0.95 }}
    animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
    transition={{ delay: index * 0.2 + 0.2, duration: 0.6 }}
    whileHover={{ y: -4 }}
  >
    <div className="relative rounded-2xl p-[2px] overflow-hidden h-full" style={{
      background: `linear-gradient(${135 + index * 45}deg, ${item.colors[0]}40, transparent 40%, transparent 60%, ${item.colors[0]}40)`,
      backgroundSize: '200% 200%',
      animation: `borderShift ${5 + index}s ease infinite`,
    }}>
      <div className="bg-white rounded-2xl p-6 h-full" data-testid={`pie-chart-${index}`}>
        <motion.h4 initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: index * 0.2 + 0.3 }}
          className="text-sm font-heading font-bold mb-4 text-center" style={{ color: NAVY }}>{item.title}</motion.h4>
        <div className="flex justify-center mb-4">
          <ResponsiveContainer width={180} height={180}>
            <PieChart>
              <Pie data={item.data} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}
                dataKey="value" animationBegin={index * 300} animationDuration={1500} aria-label={`Pie chart: ${item.title}`}>
                {item.data.map((entry, i) => (<Cell key={entry.name} fill={item.colors[i]} />))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="text-center mb-3">
          <span className="text-3xl font-bold" style={{ color: item.colors[0] }}>
            <AnimatedCounter target={item.data[0].value} inView={inView} delay={index * 200 + 400} />
          </span>
          <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: index * 0.2 + 1 }}
            className="text-xs ml-1" style={{ color: MID }}>{item.data[0].name}</motion.span>
        </div>
        <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: index * 0.2 + 1.2 }}
          className="text-xs leading-relaxed text-center" style={{ color: MID }}>{item.caption}</motion.p>
      </div>
    </div>
  </motion.div>
);

export const SurveyInsightsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="py-20 lg:py-28" style={{ background: '#E8EFF5' }} ref={ref} data-testid="survey-insights-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Animated Header */}
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-12">
          <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }} className="text-spaced mb-3 block">S U R V E Y &nbsp; I N S I G H T S</motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4" style={{ color: NAVY }}>
            <AnimatedText text="Validating the Need" inView={inView} />
          </h2>
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.6 }}
            className="text-base max-w-2xl mx-auto" style={{ color: MID }}>
            Real insights from user research showing why patient-controlled healthcare infrastructure is critical.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.8 }}
            className="text-[11px] mt-3" style={{ color: '#9CA3AF' }}>Insights from user responses</motion.p>
        </motion.div>

        {/* Pie Charts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {pieData.map((item, i) => (<PieChartCard key={item.title} item={item} index={i} inView={inView} />))}
        </div>

        {/* Bar Charts */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 1 }} className="mb-14">
          <h3 className="text-lg font-heading font-bold text-center mb-8" style={{ color: NAVY }}>
            <AnimatedText text="Common Pain Points" inView={inView} />
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              { title: "Patient & Doctor Struggles", desc: "79% struggle with medical history. Our structured timelines fix this.", data: painData, id: "pain-bar-chart" },
              { title: "Waiting Times Distribution", desc: "31% wait 30+ minutes. AI intake during waits turns dead time into data.", data: waitData, id: "wait-bar-chart" },
            ].map((chart, ci) => (
              <motion.div key={chart.id}
                initial={{ opacity: 0, x: ci === 0 ? -20 : 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: ci * 0.2 + 1.2, duration: 0.6 }}
              >
                <div className="relative rounded-2xl p-[2px] overflow-hidden" style={{
                  background: `linear-gradient(${135 + ci * 90}deg, ${TEAL}30, transparent 50%, ${TEAL}30)`,
                  backgroundSize: '200% 200%', animation: `borderShift ${6 + ci}s ease infinite`,
                }}>
                  <div className="bg-white rounded-2xl p-6" data-testid={chart.id}>
                    <motion.h4 initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: ci * 0.2 + 1.3 }}
                      className="text-sm font-bold mb-1" style={{ color: NAVY }}>{chart.title}</motion.h4>
                    <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: ci * 0.2 + 1.4 }}
                      className="text-xs mb-4" style={{ color: MID }}>{chart.desc}</motion.p>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={chart.data} layout="vertical" margin={{ left: 10, right: 20 }} aria-label={`Bar chart: ${chart.title}`}>
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: MID }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: NAVY, fontWeight: 600 }} width={ci === 0 ? 130 : 80} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]} animationDuration={1800} animationBegin={ci * 300 + 500}>
                          {chart.data.map((entry) => (<Cell key={entry.name} fill={entry.fill} />))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Animated Key Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { value: 57, label: "failed to find reports", color: RED },
            { value: 83, label: "repeat history at visits", color: ORANGE },
            { value: 79, label: "want remote management", color: GREEN },
            { value: 64, label: "would recommend", color: TEAL },
          ].map((s, i) => (
            <motion.div key={s.label}
              initial={{ opacity: 0, scale: 0.8, rotate: -2 }}
              animate={inView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
              transition={{ delay: i * 0.15 + 1.5, type: "spring", stiffness: 200 }}
              whileHover={{ scale: 1.05, y: -3 }}
              data-testid={`counter-stat-${i}`}
            >
              <div className="relative rounded-xl p-[2px] overflow-hidden" style={{
                background: `linear-gradient(135deg, ${s.color}40, transparent 50%, ${s.color}40)`,
                backgroundSize: '200% 200%', animation: `borderShift ${4 + i}s ease infinite`,
              }}>
                <div className="bg-white rounded-xl p-5 text-center">
                  <motion.p className="text-3xl font-bold mb-1"
                    style={{ color: s.color }}
                    animate={inView ? { scale: [1, 1.15, 1] } : {}}
                    transition={{ delay: i * 0.2 + 2.2, duration: 0.4 }}>
                    <AnimatedCounter target={s.value} inView={inView} delay={i * 150 + 1500} />
                  </motion.p>
                  <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.15 + 2 }}
                    className="text-[11px] leading-tight" style={{ color: MID }}>{s.label}</motion.p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 2.2 }} className="text-center">
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 2.3 }}
            className="text-sm mb-4" style={{ color: MID }}>Ready to solve these pain points for your clinic?</motion.p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button size="lg" className="btn-teal px-8" onClick={(e) => scrollTo(e, "#demo")}>
              <TrendingUp className="w-4 h-4 mr-2" /> Book a Demo
            </Button>
          </motion.div>
        </motion.div>
      </div>

      <style>{`
        @keyframes borderShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .recharts-pie-sector, .recharts-bar-rectangle { animation: none !important; }
        }
      `}</style>
    </section>
  );
};
